<template>
	<tool-dialog
		v-bind="$props"
		:searchable="true"
		:expandable="true"
		:display-name="$t('activitylog.display-name')"
		max-height="20rem"
		v-on="$listeners"
	>
		<b-list-group v-if="computedActivityLog">
			<b-list-group-item v-if="isLoading" class="d-flex flex-column align-items-stretch alternate-bg p-2">
				<b-spinner
					type="grow"
					:label="$t('activitylog.fetching-project-data')"
					class="d-block mx-auto"
				/>
			</b-list-group-item>
			<b-list-group-item
				v-for="(item, i) in computedActivityLog"
				:key="i"
				class="d-flex flex-column align-items-stretch alternate-bg p-0 pt-2 pr-1"
			>
				<div class="d-flex align-items-center mx-0 w-100 mb-1">
					<div class="text-right position-absolute fixed-top p-2">
						<b-icon
							v-model="copyIndex"
							:icon="
								copyIndex === item.id ? 'arrow-counterclockwise' : 'link45deg'
							"
							:animation="copyIndex === item.id ? 'spin-reverse' : ''"
							style="cursor: pointer"
							@click="copy(item.id).then((x) => (copyIndex = x))"
						/>
					</div>
					<b-col cols="*" class="ml-2">
						<div>
							<b-avatar
								variant="info"
								:text="(item.user.firstName[0]+item.user.lastName[0]) | capitalize"
							/>
						</div>
					</b-col>
					<b-col class="ml-3">
						<b-row>
							<small class="text-muted">
								{{ $luxon(item.timestamp, "relative") }}
							</small>
						</b-row>
						<b-row>
							{{
								`${item.user.firstName} ${item.user.lastName} ${$t('activitylog.made-changes')}:`
							}}
						</b-row>
					</b-col>
				</div>
				<b-list-group flush class="activity-list small w-100 list-group-dashed pb-2">
					<b-list-group-item v-for="logs in item.actions" :key="logs.uuid" class="bg-transparent p-0 border-0">
						{{ $t('activitylog.action')[logs.action] }}
						{{ [logs.action, logs.changeset] | makeActivityListItem }}
					</b-list-group-item>
				</b-list-group>
			</b-list-group-item>
			<b-list-group-item v-show="computedActivityLog.length === 0" class="alternate-bg text-center">
				{{
					query !== "" || activities.length === 0 ? $t('activitylog.no-matches') : `${$t('loading')}...`
				}}
			</b-list-group-item>
			<b-list-group-item
				v-show="computedActivityLog.length !== 0 && query !== ''"
				class="alternate-bg text-center"
			>
				{{ $t('activitylog.no-remaining-matches') }}
			</b-list-group-item>
		</b-list-group>
		<b-list-group v-else>
			<b-list-group-item class="alternate-bg">
				<b-spinner
					v-if="!computedActivityLog"
					type="grow"
					:label="$t('activitylog.fetching-project-data')"
					class="d-block mx-auto"
				/>
				<div v-if="activityLogStatus === 'error'" v-once>
					{{ makeToast() }}
				</div>
				{{ computedActivityLog }}
			</b-list-group-item>
		</b-list-group>
	</tool-dialog>
</template>

<script>
import BaseTool from '@/components/Project/Tool/BaseTool.vue';

export default {
	name: 'ActivityLog',
	filters: {
		makeActivityListItem(info) {
			const [action, changeset] = info;
			if (!changeset) return false;
			const parameter = changeset.name.replace(/\./g, ' → ').replace(/^_/g, '').toUpperCase();
			const crudSuffix = {
				update: `from ${JSON.stringify(changeset.oldValue)} to ${JSON.stringify(changeset.newValue)}`,
				create: `and set it to ${JSON.stringify(changeset.value)}`,
				delete: `(${JSON.stringify(changeset.value)})`,
			};
			const suffix = crudSuffix[action];
			return ` ${parameter} ${suffix}`;
		},
	},
	extends: BaseTool,
	data() {
		return {
			query: '',
			copyIndex: '',
			activities: null,
			isLoading: false,
		};
	},
	computed: {
		computedActivityLog() {
			const searchTerm = this.query.toLowerCase();

			if (searchTerm === '') {
				return this.activities;
			}

			return this.activities.filter((activity) => {
				// check if username contains the search term
				if (`${activity.user.firstName} ${activity.user.lastName}`.toLowerCase().match(searchTerm)) {
					return true;
				}

				// check if one of the actions contains the search term
				const actions = activity.actions.map((x) => x.action.toLowerCase());
				for (let i = 0; i < actions.length; i++) {
					if (actions[i].match(searchTerm)) {
						return true;
					}
				}

				return false;
			});
		},
		activityLogStatus() {
			return this.$store.getters.getActivityLogStatus;
		},
	},
	created() {
		this.$root.$on('new-activity', async () => {
			this.isLoading = true;
			await this.load();
			this.isLoading = false;
		});
	},
	mounted() {
		this.load();
	},
	methods: {
		async load() {
			this.activities = (await this.axios.get(`/scenarios/${this.scenario.id}/activities`)).data.reverse();
		},
		async copy(uuid) {
			const activityUrl = `${this.$store.getters.getBaseUrl}activity/${uuid}`;
			this.copyIndex = uuid;
			await navigator.clipboard.writeText(activityUrl);

			await this.$bvToast.toast(`${this.$t('copied-to-cliboard')}!`, {
				autoHideDelay: 1000,
				noCloseButton: true,
				variant: 'success',
				solid: true,
			});

			return '';
		},

		makeToast(append = false) {
			this.$bvToast.toast(`${this.computedActivityLog}`, {
				title: this.$t('toast.error'),
				autoHideDelay: 5000,
				appendToast: append,
				variant: 'danger',
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.list-group-dashed > .list-group-item {
	text-indent: -0.5rem;

	&:before {
		content: '-';
		text-indent: -0.5rem;
	}
}
.activity-list {
	padding-left: 4.1rem !important;
	opacity: 0.8;
}
</style>
